import { useRef } from "react";
import { useCms } from "@/lib/cms";
import { useEmbedPreview } from "@/lib/embedPreview";
import { BACKEND_BASE } from "@/lib/backend";
import {
  HERO_SPLINE_EMBED_KEY,
  isHeroSplineEnabled,
  HERO_LEFT_COPY_ENABLED,
  HERO_AI_BAR_ENABLED,
} from "@/components/sections/hero/heroShared";
import HeroSplineBgEmbed from "@/components/sections/hero/HeroSplineBgEmbed";
import HeroSplineWheelBridge from "@/components/sections/hero/HeroSplineWheelBridge";
import HeroAmbientBg from "@/components/sections/hero/HeroAmbientBg";
import HeroLeftCopy from "@/components/sections/hero/HeroLeftCopy";
import HeroAiConsultBar from "@/components/sections/hero/HeroAiConsultBar";
import "./HeroSection.css";

const absUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND_BASE}${u}`) : "");

/** Desktop / tablet-wide hero (601px+). Mobile uses HeroSectionMobile. */
export default function HeroSectionDesktop({
  showDevicePreview = true,
  embedMode: embedModeProp = false,
  previewDevice,
}) {
  const { cms } = useCms();
  const embedFromUrl = useEmbedPreview();
  const embedMode = embedModeProp || embedFromUrl;

  const heroVideo = absUrl(cms.media_hero_bg_video);
  const heroImage = absUrl(cms.media_hero_bg_image);
  const useMediaBg = Boolean(heroVideo || heroImage);
  const isInlinePreview = Boolean(previewDevice);
  const showSplineBg = isHeroSplineEnabled({ useMediaBg, embedMode, previewDevice });
  const sectionRef = useRef(null);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className={`ds-hero-section ds-hero-section--desktop${embedMode ? " ds-hero-section--embed" : ""}${isInlinePreview ? " ds-hero-section--inline-preview" : ""}`}
      id="hero"
    >
      {showSplineBg || useMediaBg ? (
        <div className="ds-hero-spline-bg-wrapper is-mounted" aria-hidden="true">
          {showSplineBg ? (
            <>
              <HeroAmbientBg />
              <HeroSplineBgEmbed key={HERO_SPLINE_EMBED_KEY} />
            </>
          ) : heroVideo ? (
            <video
              data-testid="hero-bg-video"
              src={heroVideo}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="ds-hero-bg-media"
            />
          ) : (
            <div
              data-testid="hero-bg-image"
              className="ds-hero-bg-media ds-hero-bg-media--image"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
          )}
        </div>
      ) : null}

      {showSplineBg ? <HeroSplineWheelBridge enabled={showSplineBg} sectionRef={sectionRef} /> : null}

      {HERO_LEFT_COPY_ENABLED ? (
        <div className="ds-hero-content-layer">
          <div className="ds-hero-col-left">
            <HeroLeftCopy
              embedMode={embedMode}
              showDevicePreview={showDevicePreview}
              showAiBar
              showScroll
              isInlinePreview={isInlinePreview}
            />
          </div>

          {(!embedMode || previewDevice === "desktop") && !isInlinePreview ? (
            <div className="ds-hero-col-spacer" aria-hidden="true" />
          ) : null}
        </div>
      ) : null}

      {HERO_AI_BAR_ENABLED && !embedMode && !HERO_LEFT_COPY_ENABLED ? (
        <HeroAiConsultBar pinned />
      ) : null}
    </section>
  );
}
